<template>
  <div class="c-form-item-combo">
    <CCombo ref="comboRef" v-bind="innerComboAttrs">
      <template #operation="scope">
        <slot v-bind="scope" name="action">
          <div class="combo-action-con" :class="{ 'one-action': isOneAction }" v-bind="actionsWrapperAttrs">
            <c-action :class="`action add action-${scope.$index}`" v-bind="getActionAddAttrs(scope)" />
            <c-action :class="`action del action-${scope.$index}`" v-bind="getActionRemoveAttrs(scope)" />
          </div>
        </slot>
      </template>
    </CCombo>
  </div>
</template>

<script lang="ts">
export default { inheritAttrs: false }
</script>

<script setup lang="ts">
import { _, ref, computed, watch, onMounted, useCurrentAppInstance } from '@zto/zpage'
import { useFormItem } from '@zto/zpage-ui-element'

const props = withDefaults(
  defineProps<{
    model: Record<string, any>
    prop: string
    items?: any[]
    itemsAttrs?: Record<string, any>

    comboAttrs?: Record<string, any>
    formAttrs?: Record<string, any>
    sectionAttrs?: Record<string, any>

    actions?: Record<string, any>
    isOneAction?: boolean // 只显示一个活动
    oneActionInsert?: boolean // 新增时是否insert,默认append

    noOperation?: boolean
    disabled?: boolean

    autoAppendFirst?: boolean // 自动添加第一行

    maxlength?: number

    addDisabled?: boolean | Function // 添加按钮禁用
    deleteDisabled?: boolean | Function // 删除按钮禁用
  }>(),
  {}
)

const app = useCurrentAppInstance()

const { innerAttrs } = useFormItem(props)

const CCombo = app.resolveComponent('CCombo')

const comboRef = ref()

const innerComboAttrs = computed(() => {
  const _comboAttrs = _.omit(props.comboAttrs, ['form', 'items'])

  const _formAttrs = _.deepMerge(props.formAttrs, _comboAttrs.form)
  const _itemsAttrs = _.deepMerge(props.itemsAttrs, _comboAttrs.items)

  return {
    items: props.items || [],
    formAttrs: _formAttrs,
    itemsAttrs: _itemsAttrs,
    sectionAttrs: props.sectionAttrs,
    noOperation: props.noOperation,
    disabled: props.disabled,
    ...props.comboAttrs
  }
})

const innerForm = computed(() => {
  return comboRef.value?.innerForm
})

/** 活动按钮属性 */
const actionsWrapperAttrs = computed(() => {
  return {
    ...props.comboAttrs?.actionWrapper,
    style: { ...props.comboAttrs?.actionWrapperStyle, ...props.comboAttrs?.actionWrapper?.style }
  }
})

/** 表格行 */
const comboData = computed(() => {
  return props.model[props.prop] || []
})

watch(
  () => props.model[props.prop],
  () => {
    resetComboData()
  }
)

watch(
  () => comboData.value.length,
  () => {
    if (comboData.value.length === 0) {
      resetComboData()
    }
  }
)

onMounted(() => {
  resetComboData()
})

/** 执行插入 */
function handleInsertItem(index: number, scope: any) {
  if (!props.isOneAction || props.oneActionInsert) {
    insertItem(index)
  } else {
    appendItem()
  }
}

/** 删除行 */
function handleRemoveItem(index: number, scope: any) {
  removeItem(index)
}

/** 是否允许新增 */
function checkIsAllowAdd(scope: any) {
  if (props.disabled === true) return false

  if (typeof props.addDisabled === 'function') {
    const ctx = app.useContext(scope)
    return props.addDisabled(ctx, comboData.value, props.model)
  }

  return props.addDisabled !== true && (!props.maxlength || comboData.value.length < props.maxlength)
}

/** 是否允许删除 */
function checkIsAllowRemove(scope: any) {
  if (props.disabled === true) return false

  if (typeof props.deleteDisabled === 'function') {
    const ctx = app.useContext(scope)
    return props.deleteDisabled(ctx, comboData.value, props.model)
  }

  return props.deleteDisabled !== true
}

/** 重设数据 */

async function resetComboData() {
  if (!comboRef.value) return

  if (props.model[props.prop] !== comboRef.value.comboData.data) {
    await comboRef.value.setData({ data: props.model[props.prop] })
    props.model[props.prop] = comboRef.value.comboData.data
  }

  if (props.autoAppendFirst && !comboData.value?.length && !innerForm.value?.disabled) {
    appendItem()
  }
}

/** 添加空行 */
function appendItem(data?: any) {
  comboRef.value?.appendItem(data || {})
}

/** 添加空行 */
function insertItem(index: number, data?: any) {
  comboRef.value?.insertItem(index, data)
}

/** 删除行 */
function removeItem(index: number) {
  comboRef.value?.removeItem(index)
}

/** 获取新增活动属性 */
function getActionAddAttrs(scope: any) {
  const disabled = !checkIsAllowAdd(scope)
  const trigger = () => handleInsertItem(scope.$index, scope)

  return { label: '新增', buttonType: 'default', disabled, trigger, ...props.actions?.add }
}

/** 获取删除 */
function getActionRemoveAttrs(scope: any) {
  const disabled = !checkIsAllowRemove(scope)
  const trigger = () => handleRemoveItem(scope.$index, scope)

  return { label: '删除', buttonType: 'default', disabled, trigger, ...props.actions?.delete }
}

const validate = () => {
  return comboRef.value?.validate()
}

defineExpose({
  innerCombo: comboRef.value,
  comboData: comboData.value,
  checkIsAllowRemove,
  checkIsAllowAdd,
  appendItem,
  insertItem,
  removeItem,
  resetComboData,
  validate
})
</script>

<style lang="scss" scoped>
.combo-action-con {
  .action {
    margin-left: 10px;
  }

  &.one-action {
    .action.add {
      display: none;

      &.action-0 {
        display: inline-block;
      }
    }
  }
}
</style>

<style lang="scss">
.el-form-item.is-error {
  .c-form-item-combo {
    border: 1px dotted var(--el-color-danger);
  }
}
</style>
